// @flow

/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import MessagingBox from '../../Messaging/MessagingBox/MessagingBox';
import React, {Component} from 'react';
import ThorScss from './Thor.scss';
import AppsUtil from '../../../../utils/AppsUtil';
import CCDUtil from '../../../../utils/CCDUtil';
import CompatibilityUtil from '../../../../utils/CompatibilityUtil';
import {PHSPData, analytics, additionalDownloadMessageData, promotion, errorTypes} from '../../../../constants/ccdConstants';
import Url from '../../../../utils/Url';
import CCDIngestAnalytics from '../../../../analytics/CCDIngestAnalytics';
import {getLocalizedURL} from '@intl/adobe-locales';
import {downloadingData, getContextGuid, clickData, prevVersionData, sendIngestLoggedOutPromotionExpEvent} from '../../../../utils/CCDAnalyticsUtils';
import ccdDtmAnalytics from '../../../../analytics/CCDDTMAnalytics';
import DownloadUtil from '../../../../utils/DownloadUtil';

function isPromotionPanelExperience() {
	return AppsUtil.isLoggedOutPromotionExp && Url.getParam('workflow') === promotion.panel;
}

export default class Thor extends Component {
	constructor(props) {
		super(props);
		this.productDetails = this.props.isCCH ? this.props.product : CCDUtil.getProductsData(this.props.routeParams.productId);
		this.genericPDPAnalyticEvent = props.genericPDPAnalyticEvent;
		this.installImageUrl = CCDUtil.getUrlPath('/assets/ccd/images/install_rebrand/thor_install.svg');
		this.imageOnloadHandler = this.imageOnloadHandler.bind(this);
	}

	errorCb() {
		this.props.setErrorType(errorTypes.serviceDown);
	}

	startNonCchDownloadViaThor() {
		if (this.productDetails.id === 'creative-cloud' && this.props.routeParams.pageType === 'starter') {
			DownloadUtil.downloadViaThor(PHSPData, true, this.errorCb.bind(this));
		}
		else {
			DownloadUtil.downloadViaThor(AppsUtil.appsFFCProductList[this.productDetails.sapCode], false, this.errorCb.bind(this));
		}
		ccdDtmAnalytics.reportDTMEvent(`DownloadCenter:DownloadClick:AAMInstalled:${this.productDetails.id}`, CCDUtil.getDTMEventData(), 'state');
		// Send legacy Ingest Thor download event for promotion workflow
		if (AppsUtil.isLoggedOutPromotionExp) {
			sendIngestLoggedOutPromotionExpEvent(this.props.routeParams.productId, 'ccpd-dl-start-thor');
		}
		this.sendAnalytic(analytics.downloadStartThor);
	}

	componentDidMount() {
		if (!this.props.isCCH) {
			ccdDtmAnalytics.reportDTMEvent(`DownloadCenter:Download:PageView:${this.productDetails.id}`, CCDUtil.getDTMEventData(), 'state');
			if (isPromotionPanelExperience()) {
				this.startNonCchDownloadViaThor();
			}
		}
		else {
			this.sendAnalytic(analytics.downloadStartThor);
		}
	}

	sendAnalytic(type: string) {
		const isOlderVersion = !CompatibilityUtil.isAppSupported(this.productDetails.sapCode) && CompatibilityUtil.isAnyOlderVersionSupported(this.productDetails.sapCode);

		const ccdIngestAnalytics = new CCDIngestAnalytics(this.props.environment);
		ccdIngestAnalytics.initAnalytics();
		let outcome = true;
		if (this.productDetails.id === 'lightroom-classic' && Url.getParam('photoplanRedirect')) {
			outcome = 'false-lr-cc/true-older-lr-classic';
		}

		let data = {};
		switch (type) {
			case 'downloadStartThor':
				if (this.props.isCCH) {
					return;
				}
				data = isOlderVersion ? CCDUtil.getCloneObject(prevVersionData) : CCDUtil.getCloneObject(downloadingData);
				break;
			case 'needHelpClicked' :
				data = CCDUtil.getCloneObject(clickData);
				data.subType = 'help';
				break;
		}
		data.componentName = 'Thor';
		data.productId = this.productDetails.id;
		data.contextGuid = getContextGuid(isOlderVersion, true, outcome);
		if (this.props.isCCH) {
			data.workflow = 'Apps';
			data.productName = this.productDetails.name;
		}
		ccdIngestAnalytics.genericAnalyticEvent(data);
	}

	getInstallInstructionText() {
		if (AppsUtil.isLoggedOutPromotionExp && Url.getParam('workflow') === promotion.launch) {
			return this.props.intl.formatMessage({id: 'ccd.thor.promotion.launch.instruction'}, {
				appName: this.productDetails.name,
				br: () => <br/>
			});
		}
		if (this.productDetails.id === 'creative-cloud') {
			return this.props.intl.formatMessage({id: 'ccd.old.thor.instructionCreativeCloud'}, {appName: this.productDetails.name});
		}
		if (additionalDownloadMessageData[this.productDetails.sapCode]) {
			const appName = this.productDetails.name;
			const secondaryAppName = additionalDownloadMessageData[this.productDetails.sapCode].additionalProductsList[0] ? CCDUtil.getProductsData(additionalDownloadMessageData[this.productDetails.sapCode].additionalProductsList[0]).name : undefined;
			const tertiaryAppName = additionalDownloadMessageData[this.productDetails.sapCode].additionalProductsList[1] ? CCDUtil.getProductsData(additionalDownloadMessageData[this.productDetails.sapCode].additionalProductsList[1]).name : undefined;
			return this.props.intl.formatMessage({id: additionalDownloadMessageData[this.productDetails.sapCode].thorMessageId}, {
				appName,
				secondaryAppName,
				tertiaryAppName,
				br: () => <br/>,
				b: str => <b>{str}</b>
			});
		}
		return this.props.intl.formatMessage({id: 'ccd.thor.instruction'}, {
			appName: this.productDetails.name,
			br: () => <br/>
		});
	}

	getTitle() {
		if (AppsUtil.isLoggedOutPromotionExp && Url.getParam('workflow') === promotion.launch) {
			return 'ccd.thor.promotion.launch.title';
		}
		if (this.productDetails.id === 'creative-cloud') {
			return 'ccd.old.thor.titleCreativeCloud';
		}
		return 'ccd.thor.title';
	}

	// render top blue messagings
	renderToastMessaging() {
		if (this.productDetails.id === 'muse') {
			return <MessagingBox isCCH={this.props.isCCH} key={`boxMessaging${this.productDetails.id}`} hasThor={true} environment={this.props.environment} productDetails={this.productDetails}
				messageCode='customMessageMuse' locale={this.props.locale} formatMessage={this.props.intl.formatMessage} genericPDPAnalyticEvent={this.genericPDPAnalyticEvent}/>;
		}
		if (!CompatibilityUtil.isAppSupported(this.productDetails.sapCode) && CompatibilityUtil.isAnyOlderVersionSupported(this.productDetails.sapCode)) {
			return <MessagingBox isCCH={this.props.isCCH} hasThor={true} environment={this.props.environment} productDetails={this.productDetails}
				messageCode='olderVersionMessage' locale={this.props.locale} formatMessage={this.props.intl.formatMessage} genericPDPAnalyticEvent={this.genericPDPAnalyticEvent}/>;
		}
		return null;
	}

	onHelpClick() {
		this.sendAnalytic(analytics.needHelpClicked);
	}

	downloadAppViaThor() {
		DownloadUtil.downloadProduct({sapcode:this.productDetails.sapCode, hasThor:true});
	}

	imageOnloadHandler() {
		if (this.props.isCCH) {
			this.downloadAppViaThor();
		}
		else {
			this.startNonCchDownloadViaThor();
		}
	}

	render() {
		if (isPromotionPanelExperience()) {
			const promotionText = this.props.intl.formatMessage({id: 'ccd.thor.promotion.launch.text'}, {br: () => <br/>});
			return (
				<div className={ThorScss.campaign_container}>
					<p>{promotionText}</p>
				</div>
			);
		}
		return (
			<div className={ThorScss.thor_install_container}>
				<div className={ThorScss.title_container}>
					<div className={ThorScss.app_title}>
						{this.props.intl.formatMessage({id: this.getTitle()}, {appName: this.productDetails.name})}
					</div>
				</div>
				<div className={ThorScss.install_steps_container}>
					<div className={ThorScss.install_step}>
						<img className={ThorScss.install_image} alt={`${this.props.intl.formatMessage({id: 'apps.tile.install'})} ${this.productDetails.name}`} src={this.installImageUrl} onLoad={this.imageOnloadHandler} onError={this.imageOnloadHandler} />
						<div className={`${ThorScss.install_desc_container} ${ThorScss.desc_rebrand}`}>
							<span>{this.getInstallInstructionText()}</span>
							<br />
							<a href={getLocalizedURL('https://helpx.adobe.com/download-install.html?promoid=GHMVYGTH&mv=other', this.props.locale)} target='_blank' onClick={() => {this.onHelpClick();}}>{this.props.intl.formatMessage({id: 'ccd.thor.getHelp'})}</a>
						</div>
					</div>
				</div>
				{this.renderToastMessaging()}
			</div>
		);
	}
}
